import PropTypes from "prop-types";
import { Box, Button, Card, Typography } from "@mui/material";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";

import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
} from "../../../theme/tokens";

import DatasetChips from "./DatasetChips";
import { datasetBrowsePath } from "./datasetDisplay";

export default function DatasetCard({ dataset, onOpenDetails, onBrowse }) {
  const { title: name, description } = dataset || {};
  const browsePath = datasetBrowsePath(dataset);

  if (!dataset) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 2,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderColor: HAIRLINE,
        }}
      >
        <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
          Invalid dataset
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        borderColor: HAIRLINE,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: `${RADIUS.sm}px`,
            display: "grid",
            placeItems: "center",
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
          }}
        >
          <StorageRoundedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Typography
          sx={{
            fontSize: FONT.lg,
            fontWeight: 600,
            color: INK,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={name}
        >
          {name}
        </Typography>
      </Box>

      <DatasetChips dataset={dataset} />

      {/* Description */}
      <Typography
        sx={{
          fontSize: FONT.sm,
          color: description ? INK_SECONDARY : INK_MUTED,
          lineHeight: 1.55,
          flexGrow: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "3.3em",
        }}
        title={description || ""}
      >
        {description || "No description provided."}
      </Typography>

      {/* Action */}
      <Box
        sx={{
          mt: "auto",
          pt: 1.25,
          borderTop: `1px solid ${HAIRLINE}`,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={() => onOpenDetails(dataset.id)}
          startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontSize: FONT.sm,
            fontWeight: 600,
            color: ACCENT,
            px: 0.75,
            ml: -0.75,
            "&:hover": { backgroundColor: ACCENT_TINT },
          }}
        >
          View Detail
        </Button>
        <Button
          size="small"
          disabled={!browsePath}
          onClick={() => onBrowse(dataset)}
          title={
            browsePath
              ? "Browse dataset files"
              : "File browsing is unavailable for this dataset"
          }
          startIcon={<FolderOpenOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            ml: "auto",
            textTransform: "none",
            fontSize: FONT.sm,
            fontWeight: 600,
            color: ACCENT,
          }}
        >
          Browse files
        </Button>
      </Box>
    </Card>
  );
}

DatasetCard.propTypes = {
  dataset: PropTypes.object,
  onOpenDetails: PropTypes.func.isRequired,
  onBrowse: PropTypes.func.isRequired,
};
